import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  Stack,
  Divider,
  CircularProgress,
} from '@mui/material';
import { CheckCircle as CheckIcon, EnergySavingsLeaf as LeafIcon } from '@mui/icons-material';
import { quoteRequestsApi } from '../services/api.ts';
import {
  QuoteRequestProductInterest,
  PRODUCT_INTEREST_LABELS,
  QuoteRequestCreate,
} from '../types/index.ts';

const PRODUCT_OPTIONS = Object.values(QuoteRequestProductInterest).map((v) => ({
  value: v,
  label: PRODUCT_INTEREST_LABELS[v],
}));

const extractError = (e: any): string => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => {
      const field = d.loc?.slice(1).join(' → ') || '';
      return field ? `${field}: ${d.msg}` : d.msg;
    }).join('; ');
  }
  return String(detail || 'Something went wrong. Please try again.');
};

const RequestQuotePage: React.FC = () => {
  const [form, setForm] = useState<QuoteRequestCreate>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_line_1: '',
    city: '',
    postcode: '',
    product_interest: QuoteRequestProductInterest.HEAT_PUMP,
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const set = (k: keyof QuoteRequestCreate, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await quoteRequestsApi.createPublic({
        ...form,
        phone: form.phone || undefined,
        message: form.message || undefined,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ bgcolor: 'grey.50', p: 2 }}
      >
        <Card sx={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <CardContent sx={{ py: 6 }}>
            <CheckIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Request Received!
            </Typography>
            <Typography color="text.secondary">
              Thank you — we'll review your details and be in touch within one business day with a
              personalised quote.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="flex-start"
      justifyContent="center"
      sx={{ bgcolor: 'grey.50', p: { xs: 2, sm: 4 } }}
    >
      <Box sx={{ maxWidth: 560, width: '100%' }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
          <LeafIcon color="success" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Request a Free Quote
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Heat pumps · Solar PV · Battery storage · EV charging
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Your details
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="First name"
                    value={form.first_name}
                    onChange={(e) => set('first_name', e.target.value)}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Last name"
                    value={form.last_name}
                    onChange={(e) => set('last_name', e.target.value)}
                    required
                    fullWidth
                    size="small"
                  />
                </Stack>
                <TextField
                  label="Email address"
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Phone number (optional)"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Property address
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Address line 1"
                  value={form.address_line_1}
                  onChange={(e) => set('address_line_1', e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Town / City"
                    value={form.city}
                    onChange={(e) => set('city', e.target.value)}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Postcode"
                    value={form.postcode}
                    onChange={(e) => set('postcode', e.target.value.toUpperCase())}
                    required
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 10 }}
                  />
                </Stack>
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                What are you interested in?
              </Typography>
              <Stack spacing={2}>
                <TextField
                  select
                  label="Product interest"
                  value={form.product_interest}
                  onChange={(e) => set('product_interest', e.target.value)}
                  required
                  fullWidth
                  size="small"
                >
                  {PRODUCT_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Additional information (optional)"
                  value={form.message}
                  onChange={(e) => set('message', e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  placeholder="Tell us anything relevant — property age, current heating system, budget, etc."
                />
              </Stack>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{ mt: 3 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Request my free quote'}
              </Button>

              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1.5}>
                We'll never share your details. No spam, no obligation.
              </Typography>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default RequestQuotePage;
